import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, Check, Download, FileCheck2, LockKeyhole, Send } from "lucide-react";
import { FormField, SignaturePad } from "../components/PublicFormControls";
import { FormPreviewContent } from "../components/admin/forms/FormPreview";
import {
  getFormsErrorMessage,
  getPublicFormDocument,
  openPublicForm,
  sendFormOtp,
  submitPublicForm,
  verifyFormOtp,
} from "../api/formsApi";

export default function PublicFormPage() {
  const { token } = useParams();
  const [proof, setProof] = useState("");
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [signature, setSignature] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submittedNow, setSubmittedNow] = useState(false);

  const load = useCallback(async (verification = "") => {
    setError("");
    setSubmittedNow(false);
    try {
      const data = await openPublicForm(token, verification);
      setForm(data);
      if (!data.verification_required) setValues((previous) => ({ ...data.form_data, ...previous }));
    } catch (err) {
      setError(getFormsErrorMessage(err, "This form link is not available."));
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const requestOtp = async () => {
    setWorking(true);
    setError("");
    setNotice("");
    try {
      await sendFormOtp(token);
      setOtpSent(true);
      setNotice("A verification code was sent to the phone number on this request.");
    } catch (err) {
      setOtpSent(true);
      setError(getFormsErrorMessage(err, "Could not send a verification code."));
    } finally { setWorking(false); }
  };

  const confirmOtp = async (event) => {
    event.preventDefault();
    setWorking(true);
    setError("");
    try {
      const result = await verifyFormOtp(token, otp);
      setProof(result.verification_token);
      setNotice("Phone verified.");
      await load(result.verification_token);
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not verify this code."));
    } finally { setWorking(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    setWorking(true);
    setError("");
    setNotice("");
    try {
      const result = await submitPublicForm(token, {
        revision: form.revision,
        form_data: values,
        accepted,
        signature_png: form.requires_signature ? signature : null,
      }, proof);
      setSubmittedNow(true);
      setForm((current) => ({ ...current, status: result.status, revision: current.revision + 1 }));
      setNotice(result.status === "signed" ? "Consent signed and submitted." : "Form submitted successfully.");
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not submit this form."));
      if (err?.response?.status === 409) await load(proof);
    } finally { setWorking(false); }
  };

  const getDocument = async () => {
    setWorking(true);
    setError("");
    try {
      const result = await getPublicFormDocument(token, proof);
      setDocumentUrl(result.url);
    } catch (err) {
      setError(getFormsErrorMessage(err, "Could not open the completed PDF."));
    } finally { setWorking(false); }
  };

  const content = form?.content;
  const ui = content?.ui_config || {};
  const acknowledgement = ui.consent_acknowledgement || "I have read and agree to the consent content above.";
  const signatureLabel = ui.signature_label || "Signature";
  const completed = ["signed", "submitted"].includes(form?.status);
  const consentCompleted = completed && (form.form_type === "consent" || (!form.form_type && form.requires_signature));
  const alreadySubmitted = consentCompleted || error === "Already submitted";
  const expired = error.toLowerCase().includes("no longer available") || error.includes("410");

  return <main className="min-h-screen bg-[#f3f6f4] px-4 py-8 sm:py-12" style={{ "--form-accent": ui.primary_color || "#096dea" }}>
    <div className="mx-auto max-w-2xl">
      {loading ? <div className="py-20 text-center text-sm text-slate-600">Loading secure form…</div> : alreadySubmitted ? (
        <section className="border-y border-slate-300 bg-white px-6 py-10 text-center sm:px-10">
          <FileCheck2 className="mx-auto text-emerald-700" size={28} />
          <h1 className="mt-4 text-xl font-bold text-slate-950">{submittedNow ? "Submitted" : "Already submitted"}</h1>
          <p role="status" className="mt-2 text-sm text-slate-600">Your consent form has been submitted. This link can no longer be used.</p>
        </section>
      ) : error && !form ? (
        <section className="border-y border-slate-300 bg-white px-6 py-10 text-center sm:px-10">
          <AlertCircle className="mx-auto text-rose-700" size={28} />
          <h1 className="mt-4 text-xl font-bold text-slate-950">{expired ? "Form link expired" : "Form unavailable"}</h1>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">{error}</p>
        </section>
      ) : form?.verification_required ? (
        <section className="border-y border-slate-300 bg-white px-6 py-8 sm:px-10">
          <LockKeyhole className="text-cyan-900" size={25} />
          <h1 className="mt-4 text-xl font-bold text-slate-950">Verify your phone</h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">A one-time code is required before this form can be viewed. The code will be sent to {form.contact_hint || "the phone number on the request"}.</p>
          {error && <p role="alert" className="mt-4 border-l-4 border-rose-600 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</p>}
          {notice && <p role="status" className="mt-4 text-sm text-emerald-800">{notice}</p>}
          {!otpSent ? <button type="button" onClick={requestOtp} disabled={working} className="mt-6 inline-flex min-h-11 items-center gap-2 bg-cyan-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-900 disabled:opacity-50"><Send size={16} /> Send verification code</button> : <form onSubmit={confirmOtp} className="mt-6 space-y-4">
            <label className="block text-sm font-semibold text-slate-800">Six-digit code<input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-1 block w-full max-w-xs border border-slate-300 px-3 py-2.5 text-lg tracking-widest focus:border-cyan-800 focus:outline-none" /></label>
            <div className="flex flex-wrap gap-3"><button type="submit" disabled={working || otp.length !== 6} className="inline-flex min-h-11 items-center gap-2 bg-cyan-800 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Check size={16} /> Verify code</button><button type="button" onClick={requestOtp} disabled={working} className="min-h-11 px-3 text-sm font-semibold text-slate-700 underline decoration-slate-400 underline-offset-4 disabled:opacity-50">Send another code</button></div>
          </form>}
        </section>
      ) : content ? (
        <article className="border-y border-slate-300 bg-white">
          <div className="space-y-5 px-6 py-6 sm:px-10 sm:py-8">
            {error && <div role="alert" className="flex items-start gap-2 border-l-4 border-rose-600 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"><AlertCircle size={17} className="mt-0.5 shrink-0" />{error}</div>}
            {notice && <div role="status" className="flex items-center gap-2 border-l-4 border-emerald-700 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900"><Check size={17} />{notice}</div>}
            {content.definition?.format_version !== 2 || completed ? <FormPreviewContent content={content} values={values} showFields={completed} /> : null}
            {completed ? <div className="border-t border-slate-200 pt-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900"><FileCheck2 size={18} /> {form.status === "signed" ? "Consent completed" : "Registration submitted"}</div>
              <button type="button" onClick={getDocument} disabled={working} className="mt-4 inline-flex min-h-11 items-center gap-2 bg-cyan-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-900 disabled:opacity-50"><Download size={16} /> Open completed PDF</button>
              {documentUrl && <a href={documentUrl} target="_blank" rel="noreferrer" className="ml-3 text-sm font-semibold text-cyan-900 underline">Open PDF</a>}
            </div> : <form onSubmit={submit} className="space-y-5 border-t border-slate-200 pt-5">
              {content.definition?.format_version === 2 ? <FormPreviewContent
                content={content}
                values={values}
                requiresSignature={form.requires_signature}
                renderField={(field) => <FormField field={field} value={values[field.key]} onChange={(key, value) => setValues((previous) => ({ ...previous, [key]: value }))} />}
                renderAgreement={() => <label className="flex items-start gap-2 text-sm leading-6 text-slate-700"><input type="checkbox" required checked={accepted} onChange={(event) => setAccepted(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700" /><span className="whitespace-pre-wrap">{acknowledgement}</span></label>}
                renderSignature={() => <div><h2 className="mb-2 text-sm font-semibold text-slate-800">{signatureLabel}</h2><SignaturePad onChange={setSignature} /></div>}
              /> : <>
              {(content.definition?.fields || []).map((field) => <FormField key={field.key} field={field} value={values[field.key]} onChange={(key, value) => setValues((previous) => ({ ...previous, [key]: value }))} />)}
              {form.requires_signature && <>
                <label className="flex items-start gap-2 text-sm leading-6 text-slate-700"><input type="checkbox" required checked={accepted} onChange={(event) => setAccepted(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-800 focus:ring-cyan-700" /><span className="whitespace-pre-wrap">{acknowledgement}</span></label>
                <div><h2 className="mb-2 text-sm font-semibold text-slate-800">{signatureLabel}</h2><SignaturePad onChange={setSignature} /></div>
              </>}
              </>}
              <button type="submit" disabled={working} className="inline-flex min-h-11 items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ui.primary_color || "#096dea" }}><Check size={16} /> {working ? "Submitting…" : form.requires_signature ? "Sign and submit" : "Submit form"}</button>
            </form>}
          </div>
        </article>
      ) : null}
      <p className="mt-5 text-center text-xs text-slate-500">This secure form link is private. Do not forward it to anyone else.</p>
    </div>
  </main>;
}